/**
 * @fileoverview Modal for displaying and selecting blurb alternatives
 */

import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';

const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200',
  low: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
};

const TONE_MATCH_LABELS = {
  strong: { label: 'Strong match', color: 'text-green-600 dark:text-green-400' },
  moderate: { label: 'Moderate match', color: 'text-yellow-600 dark:text-yellow-400' },
  weak: { label: 'Weak match', color: 'text-red-600 dark:text-red-400' },
};

function getConfidenceLevel(confidence) {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.6) return 'medium';
  return 'low';
}

export default function BlurbGeneratorModal({
  isOpen,
  onClose,
  alternatives = [],
  onSelect,
  onRegenerate,
  isRegenerating = false,
  isGenerating = false,
  reasoning = '',
  error = null,
}) {
  const [feedback, setFeedback] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleSelect = (index) => {
    if (onSelect && alternatives[index]) {
      onSelect(alternatives[index].blurb);
      onClose();
    }
  };

  const handleRegenerate = () => {
    if (onRegenerate && feedback.trim()) {
      onRegenerate(feedback.trim());
      setFeedback('');
    }
  };

  const handleClose = () => {
    setFeedback('');
    setSelectedIndex(null);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title className="flex items-center gap-2 text-base font-medium text-gray-900 dark:text-gray-100">
                    <SparklesIcon className="h-5 w-5 text-indigo-500" />
                    Generate Blurb
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-4 py-4 max-h-[60vh] overflow-y-auto">
                  {/* Loading State */}
                  {isGenerating && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Generating blurb alternatives...</p>
                    </div>
                  )}

                  {/* Error State */}
                  {error && !isGenerating && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
                      <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                    </div>
                  )}

                  {/* Alternatives */}
                  {!isGenerating && !error && alternatives.length > 0 && (
                    <div className="space-y-4">
                      {alternatives.map((alt, index) => {
                        const confidenceLevel = getConfidenceLevel(alt.confidence);
                        const toneMatch = TONE_MATCH_LABELS[alt.toneMatch] || TONE_MATCH_LABELS.moderate;

                        return (
                          <div
                            key={index}
                            className={`border rounded-lg p-3 transition-colors ${
                              selectedIndex === index
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Alternative {index + 1}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleSelect(index)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors"
                              >
                                <CheckIcon className="h-3.5 w-3.5" />
                                Use This
                              </button>
                            </div>

                            <p className="text-sm text-gray-700 dark:text-gray-200 mb-3 whitespace-pre-wrap">
                              {alt.blurb}
                            </p>

                            <div className="flex items-center gap-3 text-xs">
                              <span
                                className={`px-1.5 py-0.5 rounded ${CONFIDENCE_COLORS[confidenceLevel]}`}
                              >
                                {Math.round(alt.confidence * 100)}% confidence
                              </span>
                              <span className={toneMatch.color}>
                                Tone: {toneMatch.label}
                              </span>
                            </div>

                            {alt.suggestion && (
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                Tip: {alt.suggestion}
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {/* Reasoning */}
                      {reasoning && (
                        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">AI Reasoning</p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">{reasoning}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No alternatives yet */}
                  {!isGenerating && !error && alternatives.length === 0 && (
                    <div className="text-center py-8">
                      <SparklesIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Click "Generate" to create blurb alternatives
                      </p>
                    </div>
                  )}
                </div>

                {/* Regeneration Section */}
                {!isGenerating && alternatives.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Feedback for regeneration:
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Make it shorter, more skeptical, add personal anecdote..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 resize-none"
                        rows={2}
                        disabled={isRegenerating}
                      />
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  {alternatives.length > 0 && (
                    <button
                      type="button"
                      onClick={handleRegenerate}
                      disabled={isRegenerating || !feedback.trim()}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRegenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="h-3.5 w-3.5" />
                          Regenerate
                        </>
                      )}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
